import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { SplitFactory } from '@splitsoftware/splitio';
import { useAuth } from './AuthContext';
import { AuthUser } from '../types';

type UserAttributeValue = string | number | boolean;
type UserAttributes = Record<string, UserAttributeValue>;

interface FeatureFlagContextType {
  isReady: boolean;
  isFeatureEnabled: (featureName: string) => boolean;
  getFeatureTreatment: (featureName: string) => string;
  userKey: string;
  userAttributes: UserAttributes;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

interface FeatureFlagProviderProps {
  children: ReactNode;
  apiKey?: string;
  userKey?: string;
}

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({ 
  children, 
  apiKey = process.env.REACT_APP_SPLIT_API_KEY || 'localhost',
  userKey: defaultUserKey = 'anonymous-user'
}) => {
  const [isReady, setIsReady] = useState(false);
  const [client, setClient] = useState<any>(null);
  
  // Safely get auth context - handle case where AuthProvider might not be available
  let user: AuthUser | null = null;
  try {
    const authContext = useAuth();
    user = authContext?.user || null;
  } catch (error) {
    // AuthContext not available, will use anonymous user
    console.warn('⚠️ AuthContext not available in FeatureFlagProvider, using anonymous user:', error);
  }

  // Generate user key and attributes based on authenticated user
  const userKey = useMemo(() => {
    if (user?.email) {
      return user.email; // Use email as the primary user identifier for Split.io
    }
    return defaultUserKey;
  }, [user?.email, defaultUserKey]);

  const userAttributes = useMemo((): UserAttributes => {
    const attributes: UserAttributes = {};
    
    if (!user) {
      return attributes;
    }
    
    // Add email-based attributes
    if (user.email) {
      attributes.email = user.email;
      const emailDomain = user.email.split('@')[1];
      if (emailDomain) {
        attributes.email_domain = emailDomain;
      }
    }
    
    // Add basic user attributes
    if (user.display_name) attributes.display_name = user.display_name;
    if (user.user_id) attributes.user_id = user.user_id;
    
    // Add role-based attributes
    if (user.roles?.length) {
      attributes.roles = user.roles.join(',');
      attributes.is_organizer = user.roles.some((role: string) => role.toUpperCase() === 'ORGANIZER');
      attributes.is_participant = user.roles.some((role: string) => role.toUpperCase() === 'PARTICIPANT');
    }
    
    // Add team-related attributes
    if (user.current_teams) {
      attributes.team_count = user.current_teams.length;
      attributes.has_teams = user.current_teams.length > 0;
      
      if (user.current_teams.length > 0) {
        attributes.team_ids = user.current_teams.map(team => team.team_id).join(',');
      }
    }
    
    return attributes;
  }, [user]);

  useEffect(() => {
    console.log('🏁 Initializing Split.io feature flags...');
    console.log('👤 User key:', userKey);
    console.log('🏷️ User attributes:', userAttributes);
    
    // Split.io configuration for browser environment
    const config = {
      core: {
        authorizationKey: apiKey,
        key: userKey,
      },
      startup: {
        readyTimeout: 10000, // 10 seconds timeout
        requestTimeoutBeforeReady: 5000, // 5 seconds for first request
      },
      scheduler: {
        featuresRefreshRate: 60, // Refresh every 60 seconds
        segmentsRefreshRate: 60,
        impressionsRefreshRate: 300, // Send impressions every 5 minutes
        telemetryRefreshRate: 3600, // Send telemetry every hour
      },
      debug: process.env.NODE_ENV === 'development',
    };

    try {
      const factory = SplitFactory(config);
      const splitClient = factory.client();
      
      // Set up event listeners
      splitClient.on(splitClient.Event.SDK_READY, () => {
        console.log('✅ Split.io SDK is ready');
        console.log('👤 User:', userKey);
        console.log('🏷️ Attributes:', userAttributes);
        setIsReady(true);
      });

      splitClient.on(splitClient.Event.SDK_READY_TIMED_OUT, () => {
        console.warn('⚠️ Split.io SDK ready timeout - using default treatments');
        setIsReady(true); // Still set ready to true so app can function with defaults
      });

      splitClient.on(splitClient.Event.SDK_UPDATE, () => {
        console.log('🔄 Split.io SDK updated with new feature flag data');
      });

      setClient(splitClient);

      // Cleanup function
      return () => {
        console.log('🧹 Cleaning up Split.io client');
        if (splitClient) {
          splitClient.destroy();
        }
      };
    } catch (error) {
      console.error('❌ Failed to initialize Split.io:', error);
      // Set ready to true with a fallback so the app can still function
      setIsReady(true);
    }
  }, [apiKey, userKey, userAttributes]);

  const isFeatureEnabled = useCallback((featureName: string): boolean => {
    if (!client || !isReady) {
      console.log(`🏁 Feature flag ${featureName}: Using default (false) - SDK not ready`);
      return false; // Default to false when SDK is not ready
    }

    try {
      const treatment = client.getTreatment(featureName, userAttributes);
      const enabled = treatment === 'on';
      console.log(`🏁 Feature flag ${featureName}: ${enabled ? 'ENABLED' : 'DISABLED'} (treatment: ${treatment})`);
      console.log(`👤 Evaluated for user: ${userKey} with attributes:`, userAttributes);
      return enabled;
    } catch (error) {
      console.error(`❌ Error evaluating feature flag ${featureName}:`, error);
      return false; // Default to false on error
    }
  }, [client, isReady, userAttributes, userKey]);

  const getFeatureTreatment = useCallback((featureName: string): string => {
    if (!client || !isReady) {
      console.log(`🏁 Feature flag ${featureName}: Using default treatment (control) - SDK not ready`);
      return 'control'; // Default treatment when SDK is not ready
    }

    try {
      const treatment = client.getTreatment(featureName, userAttributes);
      console.log(`🏁 Feature flag ${featureName}: treatment = ${treatment}`);
      console.log(`👤 Evaluated for user: ${userKey} with attributes:`, userAttributes);
      return treatment;
    } catch (error) {
      console.error(`❌ Error getting treatment for feature flag ${featureName}:`, error);
      return 'control'; // Default treatment on error
    }
  }, [client, isReady, userAttributes, userKey]);

  const contextValue: FeatureFlagContextType = useMemo(() => ({
    isReady,
    isFeatureEnabled,
    getFeatureTreatment,
    userKey,
    userAttributes,
  }), [isReady, isFeatureEnabled, getFeatureTreatment, userKey, userAttributes]);

  return (
    <FeatureFlagContext.Provider value={contextValue}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = (): FeatureFlagContextType => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
};

// Hook specifically for the file upload feature
export const useFileUploadFeature = (): boolean => {
  const { isFeatureEnabled } = useFeatureFlags();
  return isFeatureEnabled('hackathonmgr_fileupload');
};

// Hook for testing user-specific targeting
export const useUserSpecificFeature = (featureName: string): {
  isEnabled: boolean;
  treatment: string;
  userKey: string;
  userAttributes: UserAttributes;
} => {
  const { isFeatureEnabled, getFeatureTreatment, userKey, userAttributes } = useFeatureFlags();
  return {
    isEnabled: isFeatureEnabled(featureName),
    treatment: getFeatureTreatment(featureName),
    userKey,
    userAttributes,
  };
};

// Hook for role-based feature targeting
export const useRoleBasedFeature = (featureName: string): {
  isEnabled: boolean;
  isOrganizer: boolean;
  isParticipant: boolean;
} => {
  const { isFeatureEnabled, userAttributes } = useFeatureFlags();
  return {
    isEnabled: isFeatureEnabled(featureName),
    isOrganizer: Boolean(userAttributes.is_organizer),
    isParticipant: Boolean(userAttributes.is_participant),
  };
};
